import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import Layout from '../components/Layout';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import { 
  Database, 
  Plus, 
  Edit, 
  Trash2, 
  Loader2,
  Map,
  MapPin,
  Building,
  Store,
  Package,
  Receipt,
  MessageSquare,
  ClipboardList,
  IndianRupee,
  Truck,
  Settings,
  Lock
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '../lib/utils';

const Masters = () => {
  const { api, canDelete, isSuperadmin, isAdmin } = useAuth();
  const [activeTab, setActiveTab] = useState('routes');
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({
    routes: [],
    zones: [],
    cities: [],
    assets: [],
    distributorTypes: [],
    retailerTypes: [],
    visitPurposes: [],
    outletPurposes: [],
    otherVisitPurposes: [],
    tadaRates: [],
    expenseTypes: []
  });
  const [dialogOpen, setDialogOpen] = useState(false);
  // Feature switches. Only the super admin may change them.
  const [settings, setSettings] = useState({ other_visit_enabled: false });
  const [savingSetting, setSavingSetting] = useState(false);
  // The day of the month last month's TADA closes on. A recurring cutoff, not a
  // fixed date - otherwise it needs moving by hand every month.
  const [tadaLock, setTadaLock] = useState({ tada_lock_day: null, locked_until: null });
  const [savingLock, setSavingLock] = useState(false);

  const fetchTadaLock = useCallback(() => {
    api.get('/settings/tada-lock')
      .then((res) => setTadaLock(res.data))
      .catch(() => { /* leaving it unset simply means nothing is locked */ });
  }, [api]);

  useEffect(() => { fetchTadaLock(); }, [fetchTadaLock]);

  const saveTadaLock = async (value) => {
    setSavingLock(true);
    try {
      const day = value === 'none' ? null : Number(value);
      const { data } = await api.put('/settings/tada-lock', { tada_lock_day: day });
      setTadaLock((prev) => ({ ...prev, ...data, updated_at: new Date().toISOString() }));
      toast.success(day
        ? `TADA now locks on the ${day}${['th', 'st', 'nd', 'rd'][(day % 100 - 20) % 10] || ['th', 'st', 'nd', 'rd'][day] || 'th'} of each month`
        : 'TADA lock cleared — every month is open');
      fetchTadaLock();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not change the lock');
    } finally {
      setSavingLock(false);
    }
  };
  const [editItem, setEditItem] = useState(null);
  const [formData, setFormData] = useState({});

  useEffect(() => {
    fetchAllMasters();
    api.get('/settings')
      .then((res) => setSettings(res.data || {}))
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggleOtherVisit = async (enabled) => {
    setSavingSetting(true);
    try {
      const { data } = await api.put('/settings', { other_visit_enabled: enabled });
      setSettings(data);
      toast.success(enabled ? 'Other visits are now allowed' : 'Other visits are switched off');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not change the setting');
    } finally {
      setSavingSetting(false);
    }
  };

  const fetchAllMasters = async () => {
    try {
      const endpoints = {
        routes: '/masters/routes',
        zones: '/masters/zones',
        cities: '/masters/cities',
        assets: '/masters/assets',
        distributorTypes: '/masters/distributor-types',
        retailerTypes: '/masters/retailer-types',
        visitPurposes: '/masters/visit-purposes',
        outletPurposes: '/masters/outlet-purposes',
        otherVisitPurposes: '/masters/other-visit-purposes',
        tadaRates: '/masters/tada-rates',
        expenseTypes: '/masters/expense-types'
      };
      const keys = Object.keys(endpoints);
      const responses = await Promise.all(keys.map((k) => api.get(endpoints[k])));
      setData(Object.fromEntries(keys.map((k, i) => [k, responses[i].data])));
    } catch (error) {
      console.error('Failed to fetch masters:', error);
      toast.error('Failed to load master data');
    } finally {
      setLoading(false);
    }
  };

  const masterConfig = {
    routes: {
      title: 'Routes',
      icon: Map,
      endpoint: '/masters/routes',
      fields: [{ name: 'name', label: 'Route Name', type: 'text' }]
    },
    zones: {
      title: 'Zones',
      icon: MapPin,
      endpoint: '/masters/zones',
      fields: [
        { name: 'name', label: 'Zone Name', type: 'text' },
        { name: 'route_id', label: 'Route', type: 'select', options: () => data.routes }
      ]
    },
    cities: {
      title: 'Cities',
      icon: Building,
      endpoint: '/masters/cities',
      fields: [{ name: 'name', label: 'City Name', type: 'text' }]
    },
    distributorTypes: {
      title: 'Distributor Types',
      icon: Truck,
      endpoint: '/masters/distributor-types',
      hint: 'Dropdown shown wherever a customer/distributor is added or edited.',
      fields: [{ name: 'name', label: 'Distributor Type', type: 'text' }]
    },
    retailerTypes: {
      title: 'Retailer Types',
      icon: Store,
      endpoint: '/masters/retailer-types',
      hint: 'Dropdown shown on the outlet form.',
      fields: [{ name: 'name', label: 'Retailer Type', type: 'text' }]
    },
    assets: {
      title: 'Assets',
      icon: Package,
      endpoint: '/masters/assets',
      hint: 'Asset in outlet — dropdown shown on the outlet form.',
      fields: [{ name: 'name', label: 'Asset Name', type: 'text' }]
    },
    visitPurposes: {
      title: 'Visit Purposes',
      icon: MessageSquare,
      endpoint: '/masters/visit-purposes',
      hint: 'Purpose options for a customer/distributor visit.',
      fields: [{ name: 'name', label: 'Purpose', type: 'text' }]
    },
    outletPurposes: {
      title: 'Outlet Purposes',
      icon: ClipboardList,
      endpoint: '/masters/outlet-purposes',
      hint: 'Purpose options for an outlet visit.',
      fields: [{ name: 'name', label: 'Purpose', type: 'text' }]
    },
    otherVisitPurposes: {
      title: 'Purpose Master',
      icon: ClipboardList,
      endpoint: '/masters/other-visit-purposes',
      hint: 'Purpose options for an Other Visit - the visit kind that is not against a customer/distributor or outlet.',
      fields: [{ name: 'name', label: 'Purpose', type: 'text' }]
    },
    tadaRates: {
      title: 'TADA Rates',
      icon: IndianRupee,
      endpoint: '/masters/tada-rates',
      hint: 'Per-designation rate card. Under 30 km is Headquarter, 30 km and above is Ex-Station. Outstation is claimed manually.',
      labelKey: 'designation',
      fields: [
        { name: 'designation', label: 'Designation', type: 'text' },
        { name: 'hq_per_km', label: 'Headquarter ₹/km', type: 'number' },
        { name: 'hq_da', label: 'Headquarter daily allowance ₹', type: 'number' },
        { name: 'ex_station_per_km', label: 'Ex-Station ₹/km', type: 'number' },
        { name: 'ex_station_da', label: 'Ex-Station daily allowance ₹', type: 'number' },
        { name: 'out_station_per_km', label: 'Outstation ₹/km', type: 'number' },
        { name: 'out_station_da', label: 'Outstation daily allowance ₹', type: 'number' }
      ]
    },
    expenseTypes: {
      title: 'Expense Types',
      icon: Receipt,
      endpoint: '/masters/expense-types',
      fields: [{ name: 'name', label: 'Type Name', type: 'text' }]
    }
  };

  const NUMERIC_FIELDS = new Set([
    'rate', 'hq_per_km', 'hq_da', 'ex_station_per_km',
    'ex_station_da', 'out_station_per_km', 'out_station_da'
  ]);

  const handleAdd = () => {
    setEditItem(null);
    const defaultForm = {};
    masterConfig[activeTab].fields.forEach(f => {
      defaultForm[f.name] = f.type === 'number' ? '' : '';
    });
    setFormData(defaultForm);
    setDialogOpen(true);
  };

  const handleEdit = (item) => {
    setEditItem(item);
    const editForm = {};
    masterConfig[activeTab].fields.forEach(f => {
      editForm[f.name] = item[f.name] || '';
    });
    setFormData(editForm);
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this item?')) return;
    try {
      await api.delete(`${masterConfig[activeTab].endpoint}/${id}`);
      toast.success('Item deleted');
      fetchAllMasters();
    } catch (error) {
      toast.error('Failed to delete item');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...formData };
      NUMERIC_FIELDS.forEach((f) => {
        if (f in payload) payload[f] = parseFloat(payload[f]) || 0;
      });

      if (editItem) {
        await api.put(`${masterConfig[activeTab].endpoint}/${editItem.id}`, payload);
        toast.success('Item updated');
      } else {
        await api.post(masterConfig[activeTab].endpoint, payload);
        toast.success('Item created');
      }
      setDialogOpen(false);
      fetchAllMasters();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Operation failed');
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-[60vh]">
          <Loader2 className="w-8 h-8 animate-spin text-sky-600" />
        </div>
      </Layout>
    );
  }

  // TADA Date Lock is a setting, not a CRUD master, so it has no config entry -
  // anything driven by one has to stand down while that tab is open.
  const config = masterConfig[activeTab];
  const items = data[activeTab] || [];

  return (
    <Layout>
      <div className="space-y-4 fade-in" data-testid="masters-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Master Data</h1>
            <p className="text-sm text-slate-500">Manage application master data</p>
          </div>
          {config && (
            <Button
              onClick={handleAdd}
              data-testid="add-master-btn"
              className="h-10 px-4 rounded-xl bg-sky-600 hover:bg-sky-700 text-white shadow-lg shadow-sky-200"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add {config.title.slice(0, -1)}
            </Button>
          )}
        </div>

        {/* Feature switches - super admin only */}
        {isSuperadmin && (
          <div className="bg-white border border-slate-200 rounded-2xl p-4" data-testid="feature-settings">
            <div className="flex items-start justify-between gap-4">
              <div className="min-w-0">
                <h2 className="font-medium text-slate-900 flex items-center gap-2">
                  <Settings className="w-4 h-4 text-slate-400" />
                  Other Visits
                </h2>
                <p className="text-sm text-slate-500 mt-0.5">
                  Lets field staff record a visit that is not against a customer/distributor or
                  outlet. Only the purpose and contact details are captured, and every such visit
                  still needs your approval before it counts.
                </p>
              </div>
              <Switch
                checked={Boolean(settings.other_visit_enabled)}
                onCheckedChange={toggleOtherVisit}
                disabled={savingSetting}
                data-testid="toggle-other-visit"
              />
            </div>
          </div>
        )}

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="flex flex-wrap gap-2 bg-transparent h-auto p-0">
            {Object.entries(masterConfig).map(([key, value]) => (
              <TabsTrigger
                key={key}
                value={key}
                data-testid={`tab-${key}`}
                className={cn(
                  "px-4 py-2 rounded-xl text-sm font-medium transition-all",
                  "data-[state=active]:bg-sky-600 data-[state=active]:text-white",
                  "data-[state=inactive]:bg-white data-[state=inactive]:text-slate-600 data-[state=inactive]:border data-[state=inactive]:border-slate-200"
                )}
              >
                <value.icon className="w-4 h-4 mr-2" />
                {value.title}
              </TabsTrigger>
            ))}
            {/* Not a CRUD master, but this is where people come looking for a
                setting, so it sits with them rather than hidden above. */}
            {isAdmin && (
              <TabsTrigger
                value="tadaLock"
                data-testid="tab-tadaLock"
                className={cn(
                  "px-4 py-2 rounded-xl text-sm font-medium transition-all",
                  "data-[state=active]:bg-sky-600 data-[state=active]:text-white",
                  "data-[state=inactive]:bg-white data-[state=inactive]:text-slate-600 data-[state=inactive]:border data-[state=inactive]:border-slate-200"
                )}
              >
                <Lock className="w-4 h-4 mr-2" />
                TADA Date Lock
              </TabsTrigger>
            )}
          </TabsList>

          {isAdmin && (
            <TabsContent value="tadaLock" className="mt-4">
              <div className="bg-white border border-slate-200 rounded-2xl p-5 max-w-2xl space-y-4"
                   data-testid="tada-lock-panel">
                <div>
                  <h2 className="font-medium text-slate-900 flex items-center gap-2">
                    <Lock className="w-4 h-4 text-slate-400" />
                    TADA Date Lock
                  </h2>
                  <p className="text-sm text-slate-500 mt-1">
                    The day of each month the previous month closes on. Set it to 4 and the team
                    has until the 4th to finish the month just gone — after that nothing in it can
                    be booked, withdrawn or re-priced. The current month is never locked.
                  </p>
                </div>

                <div className="flex items-end gap-4 flex-wrap">
                  <div className="w-48">
                    <Label className="text-xs text-slate-500">Lock on day</Label>
                    <Select
                      value={tadaLock.tada_lock_day ? String(tadaLock.tada_lock_day) : 'none'}
                      onValueChange={saveTadaLock}
                      disabled={savingLock}
                    >
                      <SelectTrigger className="h-11 rounded-xl" data-testid="tada-lock-day">
                        <SelectValue placeholder="Not set" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Not set (never locks)</SelectItem>
                        {Array.from({ length: 31 }, (_, i) => i + 1).map((day) => (
                          <SelectItem key={day} value={String(day)}>
                            {String(day).padStart(2, '0')}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="text-sm">
                    {tadaLock.locked_until ? (
                      <p className="text-amber-700">
                        Locked up to and including <b>{tadaLock.locked_until}</b>
                      </p>
                    ) : (
                      <p className="text-slate-500">Nothing is locked at the moment.</p>
                    )}
                    {tadaLock.updated_at && (
                      <p className="text-xs text-slate-400 mt-1">
                        Last changed {String(tadaLock.updated_at).slice(0, 10)}
                        {tadaLock.updated_by ? ` by ${tadaLock.updated_by}` : ''}
                      </p>
                    )}
                  </div>
                </div>

                <div className="rounded-xl bg-slate-50 border border-slate-200 p-3 text-xs text-slate-600">
                  <p className="font-medium text-slate-700 mb-1">What the lock stops</p>
                  <ul className="list-disc pl-4 space-y-0.5">
                    <li>Booking an Outstation or Other expense against a locked date</li>
                    <li>Withdrawing a claim that falls in a locked month</li>
                    <li>The nightly re-pricing touching a settled month</li>
                  </ul>
                </div>
              </div>
            </TabsContent>
          )}

          {Object.keys(masterConfig).map((key) => (
            <TabsContent key={key} value={key} className="mt-4 space-y-3">
              {masterConfig[key].hint && (
                <p className="text-sm text-slate-500 bg-slate-100 rounded-xl px-4 py-3">
                  {masterConfig[key].hint}
                </p>
              )}
              <div className="bg-white rounded-2xl border border-slate-100 overflow-hidden">
                {data[key]?.length === 0 ? (
                  <div className="p-8 text-center">
                    <Database className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                    <p className="text-slate-500">No {masterConfig[key].title.toLowerCase()} found</p>
                  </div>
                ) : (
                  <div className="divide-y divide-slate-100">
                    {data[key]?.map((item) => (
                      <div
                        key={item.id}
                        className="flex items-center justify-between p-4 hover:bg-slate-50 transition-colors"
                        data-testid={`master-item-${item.id}`}
                      >
                        <div>
                          <p className="font-medium text-slate-900">
                            {item[masterConfig[key].labelKey || 'name']}
                          </p>
                          {key === 'tadaRates' && (
                            <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1 text-sm text-slate-500">
                              <span>HQ ₹{item.hq_per_km}/km + ₹{item.hq_da}</span>
                              <span>Ex-Station ₹{item.ex_station_per_km}/km + ₹{item.ex_station_da}</span>
                              <span>Outstation ₹{item.out_station_per_km}/km + ₹{item.out_station_da}</span>
                            </div>
                          )}
                          {item.route_id && (
                            <p className="text-sm text-slate-500">
                              Route: {data.routes?.find(r => r.id === item.route_id)?.name || item.route_id}
                            </p>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleEdit(item)}
                            className="h-8 w-8"
                            data-testid={`edit-${item.id}`}
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          {canDelete && (
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleDelete(item.id)}
                              className="h-8 w-8 text-red-500 hover:text-red-700 hover:bg-red-50"
                              data-testid={`delete-${item.id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </TabsContent>
          ))}
        </Tabs>

        {/* Add/Edit Dialog - only ever opened from a CRUD master */}
        <Dialog open={dialogOpen && Boolean(config)} onOpenChange={setDialogOpen}>
          <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>
                {editItem ? `Edit ${config?.title?.slice(0, -1) ?? ''}`
                          : `Add ${config?.title?.slice(0, -1) ?? ''}`}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              {(config?.fields ?? []).map((field) => (
                <div key={field.name} className="space-y-2">
                  <Label>{field.label}</Label>
                  {field.type === 'select' ? (
                    <select
                      value={formData[field.name] || ''}
                      onChange={(e) => setFormData({ ...formData, [field.name]: e.target.value })}
                      className="w-full h-12 px-3 rounded-xl border border-slate-200 bg-white text-sm"
                      data-testid={`input-${field.name}`}
                    >
                      <option value="">Select {field.label}</option>
                      {field.options()?.map((opt) => (
                        <option key={opt.id} value={opt.id}>{opt.name}</option>
                      ))}
                    </select>
                  ) : (
                    <Input
                      type={field.type}
                      value={formData[field.name] || ''}
                      onChange={(e) => setFormData({ ...formData, [field.name]: e.target.value })}
                      placeholder={`Enter ${field.label.toLowerCase()}`}
                      className="h-12 rounded-xl"
                      data-testid={`input-${field.name}`}
                    />
                  )}
                </div>
              ))}
              <div className="flex gap-3 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setDialogOpen(false)}
                  className="flex-1 h-11 rounded-xl"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  className="flex-1 h-11 rounded-xl bg-sky-600 hover:bg-sky-700 text-white"
                  data-testid="save-master-btn"
                >
                  {editItem ? 'Update' : 'Create'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default Masters;
